import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

type ScopeType = 'devices' | 'flows' | 'settings' | 'logs';

type ActiveContext = ScopeContext | DeviceContext | FlowContext | AppContext | SettingContext;

interface ScopeContext {
  type: 'scope';
  scopeType: ScopeType;
  parentContext: ActiveContext | null;
}

interface DeviceContext {
  type: 'device';
  deviceId: string;
  deviceName: string;
  parentContext: ActiveContext | null;
}

interface FlowContext {
  type: 'flow';
  flowId: string;
  flowName: string;
  isAdvancedFlow: boolean;
  parentContext: ActiveContext | null;
}

interface AppContext {
  type: 'app';
  appId: string;
  appName: string;
  parentContext: ActiveContext | null;
}

interface SettingContext {
  type: 'setting';
  settingId: string;
  settingTitle: string;
  parentContext: ActiveContext | null;
}

interface CommandPaletteState {
  isOpen: boolean;
  query: string;
  activeContext: ActiveContext | null;
  highlightedKey: string | null;
}

interface CommandPaletteContextOptions {
  parentContext?: ActiveContext | null;
}

interface DeviceInput {
  id: string;
  name?: string | null;
}

interface FlowInput {
  id: string;
  name?: string | null;
}

interface AppInput {
  id: string;
  name?: string | null;
}

interface SettingInput {
  settingId: string;
  settingTitle?: string | null;
}

interface FlowContextOptions extends CommandPaletteContextOptions {
  isAdvancedFlow?: boolean;
}

interface CommandPaletteActions {
  open: () => void;
  close: () => void;
  toggle: () => void;
  setQuery: (query: string) => void;
  setHighlightedKey: (highlightedKey: string | null) => void;
  enterScopeContext: (scopeType: ScopeType, options?: CommandPaletteContextOptions) => void;
  enterDeviceContext: (
    device: DeviceInput | null | undefined,
    options?: CommandPaletteContextOptions,
  ) => void;
  enterFlowContext: (flow: FlowInput | null | undefined, options?: FlowContextOptions) => void;
  enterAppContext: (
    app: AppInput | null | undefined,
    options?: CommandPaletteContextOptions,
  ) => void;
  enterSettingContext: (
    setting: SettingInput | null | undefined,
    options?: CommandPaletteContextOptions,
  ) => void;
  exitContext: () => void;
  resetSessionState: () => void;
}

export type CommandPaletteStoreState = CommandPaletteState & CommandPaletteActions;

const initialState: CommandPaletteState = {
  isOpen: false,
  query: '',
  activeContext: null,
  highlightedKey: null,
};

function cloneContext(context: ActiveContext | null | undefined): ActiveContext | null {
  if (context == null) {
    return null;
  }

  if (context.type === 'scope') {
    return {
      type: 'scope',
      scopeType: context.scopeType,
      parentContext: cloneContext(context.parentContext),
    };
  }

  if (context.type === 'device') {
    return {
      type: 'device',
      deviceId: context.deviceId,
      deviceName: context.deviceName,
      parentContext: cloneContext(context.parentContext),
    };
  }

  if (context.type === 'flow') {
    return {
      type: 'flow',
      flowId: context.flowId,
      flowName: context.flowName,
      isAdvancedFlow: context.isAdvancedFlow === true,
      parentContext: cloneContext(context.parentContext),
    };
  }

  if (context.type === 'app') {
    return {
      type: 'app',
      appId: context.appId,
      appName: context.appName,
      parentContext: cloneContext(context.parentContext),
    };
  }

  if (context.type === 'setting') {
    return {
      type: 'setting',
      settingId: context.settingId,
      settingTitle: context.settingTitle,
      parentContext: cloneContext(context.parentContext),
    };
  }

  return null;
}

export const commandPaletteStore = createWithEqualityFn<CommandPaletteStoreState>(
  (set, get) => ({
    ...initialState,

    open() {
      set({
        isOpen: true,
      });
    },

    close() {
      set({
        isOpen: false,
        query: '',
        activeContext: null,
        highlightedKey: null,
      });
    },

    toggle() {
      if (get().isOpen === true) {
        get().close();
        return;
      }

      get().open();
    },

    setQuery(query) {
      set({ query });
    },

    setHighlightedKey(highlightedKey) {
      set({ highlightedKey });
    },

    enterScopeContext(scopeType, options = {}) {
      if (
        scopeType !== 'devices' &&
        scopeType !== 'flows' &&
        scopeType !== 'settings' &&
        scopeType !== 'logs'
      ) {
        return;
      }

      set({
        activeContext: {
          type: 'scope',
          scopeType,
          parentContext: cloneContext(options.parentContext),
        },
        query: '',
        highlightedKey: null,
      });
    },

    enterDeviceContext(device, options = {}) {
      if (device == null) {
        return;
      }

      set({
        activeContext: {
          type: 'device',
          deviceId: device.id,
          deviceName: device.name ?? device.id,
          parentContext: cloneContext(options.parentContext),
        },
        query: '',
        highlightedKey: null,
      });
    },

    enterFlowContext(flow, options = {}) {
      if (flow == null) {
        return;
      }

      set({
        activeContext: {
          type: 'flow',
          flowId: flow.id,
          flowName: flow.name ?? flow.id,
          isAdvancedFlow: options.isAdvancedFlow === true,
          parentContext: cloneContext(options.parentContext),
        },
        query: '',
        highlightedKey: null,
      });
    },

    enterAppContext(app, options = {}) {
      if (app == null) {
        return;
      }

      set({
        activeContext: {
          type: 'app',
          appId: app.id,
          appName: app.name ?? app.id,
          parentContext: cloneContext(options.parentContext),
        },
        query: '',
        highlightedKey: null,
      });
    },

    enterSettingContext(setting, options = {}) {
      if (setting == null || setting.settingId == null) {
        return;
      }

      set({
        activeContext: {
          type: 'setting',
          settingId: setting.settingId,
          settingTitle: setting.settingTitle ?? setting.settingId,
          parentContext: cloneContext(options.parentContext),
        },
        query: '',
        highlightedKey: null,
      });
    },

    exitContext() {
      const parentContext = cloneContext(get().activeContext?.parentContext);

      set({
        activeContext: parentContext,
        query: '',
        highlightedKey: null,
      });
    },

    resetSessionState() {
      set({
        query: '',
        activeContext: null,
        highlightedKey: null,
      });
    },
  }),
  shallow,
);

function selectAll(state: CommandPaletteStoreState): CommandPaletteStoreState {
  return state;
}

export function useCommandPalette<TSelected = CommandPaletteStoreState>(
  selector: (state: CommandPaletteStoreState) => TSelected = selectAll as (
    state: CommandPaletteStoreState,
  ) => TSelected,
  equalityFn: (a: TSelected, b: TSelected) => boolean = shallow,
): TSelected {
  return commandPaletteStore(selector, equalityFn);
}
